import { useState, useMemo, useEffect } from "react";
import {
  Card,
  Button,
  Badge,
  Avatar,
  Collapse,
  Typography,
  Space,
  Row,
  Col,
  Statistic,
  Popover,
  Divider,
  Popconfirm,
  Modal,
  Tag,
  Descriptions,
  App,
  Spin,
  theme,
} from "antd";
import {
  PlusOutlined,
  TeamOutlined,
  CrownOutlined,
  UserOutlined,
  MoreOutlined,
  EyeOutlined,
  EditOutlined,
  StarOutlined,
  CheckCircleOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { Consultor, ConsultorFormData, TipoConsultor } from "@/features/consultores/types/consultor";
import { ConsultorStepForm } from "@/features/consultores/components/ConsultorStepForm";
import { ConsultoresFiltros } from "@/features/consultores/components/ConsultoresFiltros";
import { useConsultoresSteps } from "@/features/consultores/hooks/useConsultoresSteps";
import { useAuth } from "@/features/auth";

const { Title, Text } = Typography;

interface TeamMember extends Consultor {
  email: string;
  telefone: string;
}

interface Team {
  id: string;
  nome: string;
  supervisor: TeamMember;
  membros: TeamMember[];
}

const tipoColors: Record<TipoConsultor, string> = {
  supervisor: "blue",
  lider: "cyan",
  relacionador: "green",
  master: "purple",
};

const tipoIcons: Record<TipoConsultor, React.ReactNode> = {
  supervisor: <CrownOutlined />,
  lider: <CheckCircleOutlined />,
  relacionador: <UserOutlined />,
  master: <StarOutlined />,
};

const tipoLabels: Record<TipoConsultor, string> = {
  supervisor: "Supervisor",
  lider: "Líder de Vendas",
  relacionador: "Relacionador",
  master: "Master",
};

export interface ConsultoresTeamPanelProps {
  /** Quando true, omite header próprio e usa layout compacto para abas. */
  embedded?: boolean;
  /**
   * Quando false, oculta “Novo Profissional” — escrita de pessoa fica só em Usuários/convite.
   * Times vira vista da hierarquia (ainda permite editar dados comerciais existentes).
   */
  allowCreate?: boolean;
}

export function ConsultoresTeamPanel({
  embedded = false,
  allowCreate = true,
}: ConsultoresTeamPanelProps) {
  const { token } = theme.useToken();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedConsultor, setSelectedConsultor] = useState<TeamMember | null>(null);
  const [openTeams, setOpenTeams] = useState<string[]>([]);
  const [tipoFilter, setTipoFilter] = useState("todos");

  const { profile } = useAuth();
  const isSuperAdmin = profile?.role === "SUPER_ADMIN";

  const {
    consultores,
    supervisores,
    loading,
    saveConsultor,
    desligarConsultor,
    tenants,
    selectedTenant,
    setSelectedTenant,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
  } = useConsultoresSteps();

  const normalizeMember = (c: Consultor): TeamMember =>
    ({
      ...c,
      email: c.email || "não informado",
      telefone: c.telefone || "não informado",
      codigo: c.codigo || "-",
    }) as TeamMember;

  const teams = useMemo<Team[]>(() => {
    const filtered = consultores.filter((c) => {
      const matchSearch =
        searchTerm === "" ||
        c.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.codigo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchTipo = tipoFilter === "todos" || c.tipo === tipoFilter;
      const matchStatus = statusFilter === "todos" || c.status === statusFilter;

      return matchSearch && matchTipo && matchStatus;
    });

    const supervisors = filtered.filter((c) => c.tipo === "supervisor");
    const others = filtered.filter((c) => c.tipo !== "supervisor");

    return supervisors.map((supervisor) => {
      const equipe = others.filter((m) => m.supervisor_id === supervisor.id);

      return {
        id: supervisor.id.toString(),
        nome: `Equipe ${supervisor.nome}`,
        supervisor: normalizeMember(supervisor),
        membros: equipe.map(normalizeMember),
      };
    });
  }, [consultores, searchTerm, tipoFilter, statusFilter]);

  const independentes = useMemo<TeamMember[]>(() => {
    return consultores
      .filter((c) => {
        const isIndependente =
          (c.tipo === "relacionador" || c.tipo === "master" || c.tipo === "lider") &&
          !c.supervisor_id;
        if (!isIndependente) return false;

        const matchSearch =
          searchTerm === "" ||
          c.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.codigo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.email?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchTipo = tipoFilter === "todos" || c.tipo === tipoFilter;
        const matchStatus = statusFilter === "todos" || c.status === statusFilter;

        return matchSearch && matchTipo && matchStatus;
      })
      .map(normalizeMember);
  }, [consultores, searchTerm, tipoFilter, statusFilter]);

  useEffect(() => {
    if (teams.length > 0 && openTeams.length === 0) {
      setOpenTeams(teams.map((t) => t.id));
    }
  }, [teams, openTeams.length]);

  const handleOpenDialog = (consultor?: TeamMember) => {
    setSelectedConsultor(consultor ?? null);
    setIsDialogOpen(true);
  };

  const handleOpenViewModal = (consultor: TeamMember) => {
    setSelectedConsultor(consultor);
    setIsViewModalOpen(true);
  };

  const handleSave = async (data: ConsultorFormData) => {
    const result = await saveConsultor(data, selectedConsultor?.id?.toString());
    if (result.success) {
      setIsDialogOpen(false);
      setSelectedConsultor(null);
    }
  };

  const handleDesligar = async (id: string) => {
    await desligarConsultor(id);
  };

  const totalProfissionais = consultores.length;
  const totalSupervisores = consultores.filter((c) => c.tipo === "supervisor").length;
  const totalLideres = consultores.filter((c) => c.tipo === "lider").length;
  const totalRelacionadores = consultores.filter(
    (c) => c.tipo === "relacionador" || c.tipo === "master",
  ).length;

  const renderActions = (consultor: TeamMember) => (
    <Space orientation="vertical" size={2} style={{ width: "100%" }}>
      <Button
        type="text"
        size="small"
        icon={<EyeOutlined />}
        style={{ width: "100%", textAlign: "left", color: token.colorText }}
        onClick={() => handleOpenViewModal(consultor)}
      >
        Visualizar
      </Button>
      <Button
        type="text"
        size="small"
        icon={<EditOutlined />}
        style={{ width: "100%", textAlign: "left", color: token.colorText }}
        onClick={() => handleOpenDialog(consultor)}
      >
        Editar
      </Button>
      <Divider style={{ margin: "4px 0", borderColor: token.colorBorder }} />
      <Popconfirm
        title="Desligar Profissional"
        description={`Deseja desligar ${consultor.nome}? Ele será removido da equipe e desativado.`}
        onConfirm={() => handleDesligar(consultor.id.toString())}
        okText="Sim"
        cancelText="Não"
        okButtonProps={{ danger: true }}
      >
        <Button
          type="text"
          size="small"
          danger
          icon={<TeamOutlined />}
          style={{ width: "100%", textAlign: "left" }}
        >
          Desligar
        </Button>
      </Popconfirm>
    </Space>
  );

  const ProfessionalItem = ({
    membro,
    variant = "default",
  }: {
    membro: TeamMember;
    variant?: "default" | "supervisor";
  }) => {
    const bgColor = variant === "supervisor"
      ? token.colorBgBase
      : token.colorBgContainer;
    const borderColor = variant === "supervisor"
      ? token.colorPrimaryBorder
      : token.colorBorder;
    const avatarBg = variant === "supervisor" ? token.colorPrimary : token.colorBgBase;

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px',
          borderRadius: '12px',
          border: `1px solid ${borderColor}`,
          backgroundColor: bgColor,
          transition: 'all 0.2s',
          cursor: 'default'
        }}
        onMouseEnter={(e) => {
          if (variant !== "supervisor") {
            (e.currentTarget as HTMLElement).style.borderColor = token.colorPrimary;
            (e.currentTarget as HTMLElement).style.boxShadow = token.boxShadow;
          }
        }}
        onMouseLeave={(e) => {
          if (variant !== "supervisor") {
            (e.currentTarget as HTMLElement).style.borderColor = borderColor;
            (e.currentTarget as HTMLElement).style.boxShadow = 'none';
          }
        }}
      >
        <div className="flex items-center gap-4">
          <Avatar
            size={variant === "supervisor" ? 48 : 44}
            style={{
              backgroundColor: avatarBg,
              color: variant === "supervisor" ? '#fff' : token.colorTextSecondary
            }}
          >
            {membro.nome
              .split(" ")
              .map((n) => n[0])
              .join("")
              .slice(0, 2)
              .toUpperCase()}
          </Avatar>
          <div>
            <Space align="center" size={8} wrap>
              <Text strong style={{ fontSize: variant === "supervisor" ? "16px" : "14px", color: token.colorText }}>
                {membro.nome}
              </Text>
              {membro.codigo && membro.codigo !== "-" && (
                <Tag color="default" className="m-0">
                  {membro.codigo}
                </Tag>
              )}
              <Tag color={tipoColors[membro.tipo]} icon={tipoIcons[membro.tipo]} className="m-0">
                {tipoLabels[membro.tipo]}
              </Tag>
              <Tag color={membro.status === "ativo" ? "success" : "error"} className="m-0">
                {membro.status === "ativo" ? "Ativo" : "Inativo"}
              </Tag>
            </Space>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1" style={{ opacity: 0.7 }}>
              <Text type="secondary" style={{ fontSize: "12px", color: token.colorTextSecondary }}>
                <MailOutlined className="mr-1" /> {membro.email}
              </Text>
              <Text type="secondary" style={{ fontSize: "12px", color: token.colorTextSecondary }}>
                <PhoneOutlined className="mr-1" /> {membro.telefone}
              </Text>
              <Text type="secondary" style={{ fontSize: "12px", color: token.colorTextSecondary }}>
                <CalendarOutlined className="mr-1" /> Cadastrado em{" "}
                {new Date(membro.created_at).toLocaleDateString("pt-BR")}
              </Text>
            </div>
          </div>
        </div>
        <Popover
          content={renderActions(membro)}
          trigger="click"
          placement="bottomRight"
          styles={{ container: { padding: 4 } }}
        >
          <Button
            type="text"
            size="large"
            icon={<MoreOutlined style={{ fontSize: 20, color: token.colorTextSecondary }} />}
            style={{ color: token.colorTextSecondary }}
          />
        </Popover>
      </div>
    );
  };

  const collapseItems = useMemo(
    () =>
      teams.map((team) => ({
        key: team.id,
        label: (
          <Space size={16} className="py-2">
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: token.colorPrimaryBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: token.colorPrimary
              }}
            >
              <TeamOutlined style={{ fontSize: 20 }} />
            </div>
            <div>
              <Text strong style={{ fontSize: 16, color: token.colorText }}>
                {team.nome}
              </Text>
              <div style={{ marginTop: -4 }}>
                <Text type="secondary" style={{ fontSize: "12px", color: token.colorTextSecondary }}>
                  <CrownOutlined className="mr-1" /> {team.supervisor.nome} •{" "}
                  {team.membros.length + 1} profissionais
                </Text>
              </div>
            </div>
          </Space>
        ),
        children: (
          <div className="space-y-4 pb-4">
            <ProfessionalItem membro={team.supervisor} variant="supervisor" />

            <Divider plain style={{ borderColor: token.colorBorder }}>
              <Text type="secondary" style={{ fontSize: 12, color: token.colorTextSecondary }}>
                Equipe de Vendas
              </Text>
            </Divider>

            <div className="grid grid-cols-1 gap-3">
              {team.membros.length > 0 ? (
                team.membros.map((membro) => (
                  <ProfessionalItem key={membro.id} membro={membro} />
                ))
              ) : (
                <div
                  style={{
                    padding: '32px',
                    textAlign: 'center',
                    backgroundColor: token.colorBgBase,
                    borderRadius: '12px',
                    border: `1px dashed ${token.colorBorder}`
                  }}
                >
                  <Text type="secondary" style={{ color: token.colorTextSecondary }}>
                    Nenhum lider ou relacionador vinculado a esta equipe.
                  </Text>
                </div>
              )}
            </div>
          </div>
        ),
        style: {
          backgroundColor: token.colorBgContainer,
          borderRadius: '12px',
          marginBottom: '12px',
          boxShadow: token.boxShadow,
          overflow: 'hidden',
          border: `1px solid ${token.colorBorder}`
        }
      })),
    [teams, token],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Space orientation="vertical" align="center">
          <Badge status="processing" size="default" />
          <Text type="secondary" style={{ color: token.colorTextSecondary }}>Carregando profissionais...</Text>
        </Space>
      </div>
    );
  }

  return (
    <App>
      <Spin spinning={loading} tip="Carregando profissionais...">
        {!embedded && allowCreate && (
          <div className="px-6 pt-2 pb-0 flex justify-end">
            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              onClick={() => handleOpenDialog()}
              className="rounded-[12px] bg-[linear-gradient(135deg,#FF3737_0%,#D92B2B_100%)] border-none shadow-[0_4px_12px_rgba(255,55,55,0.3)] hover:scale-[1.02] transition-all"
            >
              Novo Profissional
            </Button>
          </div>
        )}

        <div className={embedded ? "p-0" : "p-6 max-w-[1600px] mx-auto"}>
          <Space orientation="vertical" size={embedded ? 24 : 32} style={{ width: "100%" }}>
            {embedded && allowCreate && (
              <div className="flex justify-end">
                <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenDialog()}>
                  Novo Profissional
                </Button>
              </div>
            )}
            {embedded && !allowCreate && (
              <Text type="secondary" className="text-sm">
                Vista da hierarquia comercial. Para adicionar pessoa com login, use a aba Usuários.
              </Text>
            )}

            <Row gutter={[16, 16]}>
              <Col xs={24} md={6}>
                <Card
                  variant="borderless"
                  className="rounded-[20px] bg-white/70 dark:bg-zinc-900/60 backdrop-blur-md border border-[rgba(255,55,55,0.1)] dark:border-white/10 shadow-sm hover:shadow-md transition-all duration-300"
                >
                  <Statistic
                    title={
                      <span className="text-[0.65rem] font-bold text-[#64748b] dark:text-zinc-400 uppercase tracking-widest">
                        Total de profissionais
                      </span>
                    }
                    value={totalProfissionais}
                    prefix={
                      <div className="p-2 rounded-[10px] bg-[#FF3737]/10 dark:bg-[#FF3737]/20 mr-2">
                        <TeamOutlined className="text-[#FF3737] dark:text-[#FF3737]" />
                      </div>
                    }
                    styles={{ content: { color: "inherit", fontWeight: 800, fontSize: "1.75rem" } }}
                  />
                </Card>
              </Col>
              <Col xs={24} md={6}>
                <Card
                  variant="borderless"
                  className="rounded-[20px] bg-white/70 dark:bg-zinc-900/60 backdrop-blur-md border border-[rgba(255,55,55,0.1)] dark:border-white/10 shadow-sm hover:shadow-md transition-all duration-300"
                >
                  <Statistic
                    title={
                      <span className="text-[0.65rem] font-bold text-[#64748b] dark:text-zinc-400 uppercase tracking-widest">
                        Supervisores
                      </span>
                    }
                    value={totalSupervisores}
                    prefix={
                      <div className="p-2 rounded-[10px] bg-amber-50 dark:bg-amber-500/10 mr-2">
                        <CrownOutlined className="text-amber-600 dark:text-amber-400" />
                      </div>
                    }
                    suffix={
                      <Text type="secondary" style={{ fontSize: "14px", marginLeft: "8px" }}>
                        {teams.length} equipes
                      </Text>
                    }
                    styles={{ content: { color: "inherit", fontWeight: 800, fontSize: "1.75rem" } }}
                  />
                </Card>
              </Col>
              <Col xs={24} md={6}>
                <Card
                  variant="borderless"
                  className="rounded-[20px] bg-white/70 dark:bg-zinc-900/60 backdrop-blur-md border border-[rgba(255,55,55,0.1)] dark:border-white/10 shadow-sm hover:shadow-md transition-all duration-300"
                >
                  <Statistic
                    title={
                      <span className="text-[0.65rem] font-bold text-[#64748b] dark:text-zinc-400 uppercase tracking-widest">
                        Líderes de vendas
                      </span>
                    }
                    value={totalLideres}
                    prefix={
                      <div className="p-2 rounded-[10px] bg-cyan-50 dark:bg-cyan-500/10 mr-2">
                        <CheckCircleOutlined className="text-cyan-600 dark:text-cyan-400" />
                      </div>
                    }
                    styles={{ content: { color: "inherit", fontWeight: 800, fontSize: "1.75rem" } }}
                  />
                </Card>
              </Col>
              <Col xs={24} md={6}>
                <Card
                  variant="borderless"
                  className="rounded-[20px] bg-white/70 dark:bg-zinc-900/60 backdrop-blur-md border border-[rgba(255,55,55,0.1)] dark:border-white/10 shadow-sm hover:shadow-md transition-all duration-300"
                >
                  <Statistic
                    title={
                      <span className="text-[0.65rem] font-bold text-[#64748b] dark:text-zinc-400 uppercase tracking-widest">
                        Relacionadores e masters
                      </span>
                    }
                    value={totalRelacionadores}
                    prefix={
                      <div className="p-2 rounded-[10px] bg-green-50 dark:bg-green-500/10 mr-2">
                        <UserOutlined className="text-green-600 dark:text-green-400" />
                      </div>
                    }
                    styles={{ content: { color: "inherit", fontWeight: 800, fontSize: "1.75rem" } }}
                  />
                </Card>
              </Col>
            </Row>

            <ConsultoresFiltros
              isSuperAdmin={isSuperAdmin}
              searchTerm={searchTerm}
              tipoFilter={tipoFilter}
              statusFilter={statusFilter}
              selectedTenant={selectedTenant}
              tenants={tenants.map((t) => ({ id: t.id.toString(), nome: t.nome }))}
              onSearchTermChange={setSearchTerm}
              onTipoFilterChange={setTipoFilter}
              onStatusFilterChange={setStatusFilter}
              onSelectedTenantChange={(val) => setSelectedTenant(val)}
            />

            {teams.length > 0 && (
              <div>
                <Title level={4} className="mb-4">
                  Estrutura de Equipes
                </Title>
                <Collapse
                  activeKey={openTeams}
                  onChange={(keys) => setOpenTeams(keys as string[])}
                  expandIconPlacement="end"
                  ghost
                  items={collapseItems}
                  className="teams-collapse"
                />
              </div>
            )}

            {independentes.length > 0 && (
              <div>
                <Title level={4} className="mb-4">
                  Profissionais Independentes
                </Title>
                <Card
                  variant="borderless"
                  style={{
                    backgroundColor: token.colorBgContainer,
                    borderRadius: '12px',
                    border: `1px solid ${token.colorBorder}`,
                    boxShadow: token.boxShadow
                  }}
                  styles={{ body: { padding: "24px" } }}
                >
                  <div className="grid grid-cols-1 gap-3">
                    {independentes.map((profissional) => (
                      <ProfessionalItem key={profissional.id} membro={profissional} />
                    ))}
                  </div>
                </Card>
              </div>
            )}

            {teams.length === 0 && independentes.length === 0 && (
              <Card
                variant="borderless"
                style={{
                  padding: '48px',
                  textAlign: 'center',
                  borderRadius: '12px',
                  backgroundColor: token.colorBgContainer,
                  boxShadow: token.boxShadow
                }}
              >
                <Space orientation="vertical" align="center" size={16}>
                  <TeamOutlined style={{ fontSize: 64, color: token.colorTextTertiary }} />
                  <div>
                    <Title level={4}>Nenhum profissional encontrado</Title>
                    <Text type="secondary">
                      {allowCreate
                        ? 'Tente ajustar seus filtros ou cadastre um novo profissional.'
                        : 'Cadastre membros na aba Usuários; a hierarquia aparece aqui automaticamente.'}
                    </Text>
                  </div>
                  {allowCreate && (
                    <Button type="primary" onClick={() => handleOpenDialog()}>
                      Novo Profissional
                    </Button>
                  )}
                </Space>
              </Card>
            )}
          </Space>

          <Modal
            open={isDialogOpen}
            onCancel={() => setIsDialogOpen(false)}
            footer={null}
            width={1000}
            style={{ top: 20 }}
            destroyOnHidden
            title={selectedConsultor ? "Editar Profissional" : "Novo Profissional"}
            className="rounded-xl overflow-hidden"
            styles={{
              content: { backgroundColor: token.colorBgContainer },
              header: { backgroundColor: token.colorBgContainer, borderColor: token.colorBorder },
            }}
          >
            <ConsultorStepForm
              consultor={selectedConsultor}
              supervisores={supervisores}
              isSuperAdmin={isSuperAdmin}
              defaultTenantId={profile?.tenant_id?.toString()}
              tenants={tenants}
              onSubmit={handleSave}
              onCancel={() => setIsDialogOpen(false)}
            />
          </Modal>

          <Modal
            title="Detalhes do Profissional"
            open={isViewModalOpen}
            onCancel={() => setIsViewModalOpen(false)}
            footer={[
              <Button key="close" size="large" onClick={() => setIsViewModalOpen(false)}>
                Fechar
              </Button>,
              <Button
                key="edit"
                type="primary"
                size="large"
                onClick={() => {
                  setIsViewModalOpen(false);
                  handleOpenDialog(selectedConsultor!);
                }}
              >
                Editar
              </Button>,
            ]}
            width={700}
            centered
            className="rounded-xl"
            styles={{
              content: { backgroundColor: token.colorBgContainer },
              header: { backgroundColor: token.colorBgContainer, borderColor: token.colorBorder },
              footer: { backgroundColor: token.colorBgContainer, borderColor: token.colorBorder },
            }}
          >
            {selectedConsultor && (
              <Space orientation="vertical" size={24} style={{ width: "100%" }}>
                <div className="flex items-center gap-6">
                  <Avatar
                    size={80}
                    style={{ backgroundColor: token.colorPrimary, fontSize: '24px', color: '#fff' }}
                  >
                    {selectedConsultor.nome
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase()}
                  </Avatar>
                  <div>
                    <Title level={3} style={{ margin: 0, color: token.colorText }}>
                      {selectedConsultor.nome}
                    </Title>
                    <Space style={{ marginTop: 8 }} wrap>
                      <Tag
                        color={tipoColors[selectedConsultor.tipo]}
                        icon={tipoIcons[selectedConsultor.tipo]}
                        className="px-3 py-1"
                      >
                        {tipoLabels[selectedConsultor.tipo]}
                      </Tag>
                      <Tag className="px-3 py-1">{selectedConsultor.codigo}</Tag>
                      <Tag
                        color={selectedConsultor.status === "ativo" ? "success" : "error"}
                        className="px-3 py-1"
                      >
                        {selectedConsultor.status === "ativo" ? "ATIVO" : "INATIVO"}
                      </Tag>
                    </Space>
                  </div>
                </div>

                <Divider style={{ borderColor: token.colorBorder }} />

                <Descriptions
                  title={<Space style={{ color: token.colorText }}><MailOutlined /> Contato</Space>}
                  bordered
                  column={1}
                  style={{ backgroundColor: token.colorBgBase }}
                >
                  <Descriptions.Item label="E-mail">{selectedConsultor.email}</Descriptions.Item>
                  <Descriptions.Item label="Telefone">{selectedConsultor.telefone}</Descriptions.Item>
                </Descriptions>

                <Descriptions
                  title={
                    <Space style={{ color: token.colorText }}>
                      <CalendarOutlined /> Dados do Sistema
                    </Space>
                  }
                  bordered
                  column={1}
                  style={{ backgroundColor: token.colorBgBase }}
                >
                  <Descriptions.Item label="Função">
                    {tipoLabels[selectedConsultor.tipo]}
                  </Descriptions.Item>
                  {selectedConsultor.supervisor_id && (
                    <Descriptions.Item label="Supervisor Associado">
                      {supervisores.find((s) => s.id === selectedConsultor.supervisor_id)?.nome ||
                        selectedConsultor.supervisor_id}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Data de Cadastro">
                    {new Date(selectedConsultor.created_at).toLocaleString("pt-BR")}
                  </Descriptions.Item>
                </Descriptions>
              </Space>
            )}
          </Modal>

          <style
            dangerouslySetInnerHTML={{
              __html: `
          .teams-collapse .ant-collapse-item {
            border-bottom: none !important;
            margin-bottom: 12px !important;
          }
          .teams-collapse .ant-collapse-header {
            padding: 12px 24px !important;
            align-items: center !important;
            background: ${token.colorBgContainer} !important;
            border: 1px solid ${token.colorBorder} !important;
            border-radius: 12px !important;
            display: flex !important;
            color: ${token.colorText} !important;
          }
          .teams-collapse .ant-collapse-header:hover {
            border-color: ${token.colorPrimaryBorder} !important;
            box-shadow: ${token.boxShadow} !important;
          }
          .teams-collapse .ant-collapse-content-box {
            padding: 16px 24px 24px 24px !important;
            background: ${token.colorBgContainer} !important;
            border-radius: 0 0 12px 12px !important;
          }
          .ant-collapse-ghost > .ant-collapse-item > .ant-collapse-content > .ant-collapse-content-box {
            padding-top: 12px !important;
          }
        `,
            }}
          />
        </div>
      </Spin>
    </App>
  );
}
